import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  LockKeyIcon, StackPlusIcon, DownloadSimpleIcon, ArrowLeftIcon,
  QrCodeIcon, PackageIcon, LightningIcon,
} from "@phosphor-icons/react";
import { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const KEY_STORE = "dcode_internal_key";

function ikey() { return localStorage.getItem(KEY_STORE) || ""; }
function client() {
  return axios.create({ baseURL: `${API_BASE}/internal`, headers: { "X-Internal-Key": ikey() } });
}

function KeyGate({ onUnlock }) {
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.get(`${API_BASE}/internal/batches`, { headers: { "X-Internal-Key": val.trim() } });
      localStorage.setItem(KEY_STORE, val.trim());
      onUnlock();
    } catch {
      toast.error("Invalid internal key");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <motion.form
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        onSubmit={submit} className="w-full max-w-sm border border-border bg-card p-8 space-y-6"
      >
        <div>
          <div className="flex items-center gap-2 text-primary mb-4"><LockKeyIcon weight="fill" size={22} /><span className="font-mono text-xs uppercase tracking-[0.25em]">Internal only</span></div>
          <h1 className="font-heading font-black text-3xl tracking-tighter leading-none">Fulfilment console</h1>
          <p className="text-white/50 mt-3 text-sm">Code generation, batches & printer manifests. Staff access.</p>
        </div>
        <Input
          data-testid="internal-key-input"
          type="password"
          placeholder="Internal key"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          className="bg-secondary/40 border-border font-mono focus-visible:ring-primary"
        />
        <Button data-testid="internal-key-submit" type="submit" disabled={busy} className="w-full bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-colors">
          {busy ? "CHECKING…" : "UNLOCK"}
        </Button>
      </motion.form>
    </div>
  );
}

function BatchDetail({ batchId, onBack }) {
  const [batch, setBatch] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await client().get(`/batches/${batchId}`); setBatch(data); }
    catch { toast.error("Could not load batch"); }
  }, [batchId]);
  useEffect(() => { load(); }, [load]);

  const downloadManifest = async () => {
    try {
      const { data } = await client().get(`/batches/${batchId}/manifest-link`, { params: { format: "csv" } });
      const a = document.createElement("a");
      a.href = data.manifest_url;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Signed manifest link opened", { description: `Link expires in ${Math.round(data.expires_in_seconds / 60)} min` });
    } catch { toast.error("Could not generate manifest link"); }
  };

  if (!batch) return <p className="font-mono text-sm text-primary animate-pulse">LOADING…</p>;

  return (
    <div>
      <button data-testid="batch-back-btn" onClick={onBack} className="flex items-center gap-1.5 font-mono text-xs text-white/50 hover:text-primary transition-colors mb-6">
        <ArrowLeftIcon weight="bold" /> ALL BATCHES
      </button>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <h2 className="font-heading font-black text-3xl tracking-tighter leading-none">{batch.name}</h2>
          <p className="font-mono text-xs text-white/40 mt-2">{batch.product || "—"} · {batch.quantity} codes</p>
        </div>
        <Button data-testid="manifest-download-btn" onClick={downloadManifest} className="bg-accent text-accent-foreground font-bold hover:bg-accent/90 transition-colors">
          <DownloadSimpleIcon weight="bold" className="mr-1.5" /> PRINTER MANIFEST (CSV)
        </Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {(batch.items || []).map((it) => (
          <div key={it.code} data-testid={`batch-item-${it.code}`} className="border border-border bg-card p-4 flex flex-col items-center">
            <img src={it.qr_url} alt={`QR ${it.code}`} className="w-28 h-28 bg-white p-1.5" />
            <p className="font-mono text-sm text-primary mt-3">/{it.code}</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mt-1">{it.status}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Internal() {
  const [unlocked, setUnlocked] = useState(!!ikey());
  const [batches, setBatches] = useState(null);
  const [selected, setSelected] = useState(null);
  const [name, setName] = useState("");
  const [product, setProduct] = useState("");
  const [qty, setQty] = useState(10);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await client().get("/batches"); setBatches(data); }
    catch (e) { if (e?.response?.status === 401) { localStorage.removeItem(KEY_STORE); setUnlocked(false); } else setBatches([]); }
  }, []);
  useEffect(() => { if (unlocked) load(); }, [unlocked, load]);

  const create = async () => {
    if (!name.trim()) return toast.error("Batch name required");
    setBusy(true);
    try {
      await client().post("/batches", { name: name.trim(), product: product.trim() || null, quantity: Number(qty) });
      toast.success(`Batch created · ${qty} codes generated`);
      setName(""); setProduct(""); setQty(10);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not create batch"); }
    finally { setBusy(false); }
  };

  if (!unlocked) return <KeyGate onUnlock={() => setUnlocked(true)} />;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-background border-b border-border">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-heading font-black tracking-tighter text-lg">
            <LightningIcon weight="fill" className="text-primary" size={22} /> D CODE
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent ml-2 border border-accent/40 px-2 py-0.5">Fulfilment</span>
          </div>
          <button data-testid="internal-logout" onClick={() => { localStorage.removeItem(KEY_STORE); setUnlocked(false); }} className="font-mono text-xs text-white/50 hover:text-primary transition-colors">LOCK</button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {selected ? (
          <BatchDetail batchId={selected} onBack={() => { setSelected(null); load(); }} />
        ) : (
          <>
            <div className="mb-10">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary mb-3">// Internal · code generation</p>
              <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">Batches</h1>
              <p className="text-white/50 mt-4 max-w-xl">Generate pools of permanent codes for printing. Codes stay <span className="text-white">unallocated</span> until a paid order claims one — carts never burn codes. QR images are generated server-side; the only export is the printer manifest.</p>
            </div>

            <div className="border border-border bg-card p-6 mb-10">
              <div className="flex items-center gap-2 text-primary mb-5"><StackPlusIcon weight="duotone" size={20} /><span className="font-mono text-[11px] uppercase tracking-[0.2em]">New batch</span></div>
              <div className="grid sm:grid-cols-4 gap-4">
                <div className="sm:col-span-2 space-y-2">
                  <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">Batch name</label>
                  <Input data-testid="batch-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Summer 2026 drop" className="bg-secondary/40 border-border focus-visible:ring-primary" />
                </div>
                <div className="space-y-2">
                  <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">Product</label>
                  <Input data-testid="batch-product-input" value={product} onChange={(e) => setProduct(e.target.value)} placeholder="Enamel pin" className="bg-secondary/40 border-border focus-visible:ring-primary" />
                </div>
                <div className="space-y-2">
                  <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">Quantity</label>
                  <Input data-testid="batch-qty-input" type="number" min={1} max={1000} value={qty} onChange={(e) => setQty(e.target.value)} className="bg-secondary/40 border-border font-mono focus-visible:ring-primary" />
                </div>
              </div>
              <Button data-testid="batch-create-btn" onClick={create} disabled={busy} className="mt-5 bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-colors">
                <QrCodeIcon weight="bold" className="mr-1.5" /> {busy ? "GENERATING…" : "GENERATE CODES"}
              </Button>
            </div>

            {batches === null ? (
              <p className="font-mono text-sm text-primary animate-pulse">LOADING…</p>
            ) : batches.length === 0 ? (
              <div className="border border-border flex flex-col items-center text-center py-16">
                <PackageIcon size={42} weight="duotone" className="text-white/30" />
                <p className="font-heading font-bold text-xl mt-4">No batches yet</p>
                <p className="text-white/40 text-sm mt-1">Generate your first batch of codes above.</p>
              </div>
            ) : (
              <div className="border border-border">
                {batches.map((b) => (
                  <div key={b.id} data-testid={`batch-row-${b.id}`} onClick={() => setSelected(b.id)} className="grid grid-cols-12 items-center gap-4 border-b border-border px-6 py-5 hover:bg-secondary/30 transition-colors cursor-pointer">
                    <div className="col-span-6 md:col-span-5 flex items-center gap-3">
                      <div className="h-10 w-10 flex items-center justify-center border border-primary/40 text-primary"><PackageIcon weight="bold" /></div>
                      <div className="min-w-0">
                        <p className="font-heading font-bold truncate">{b.name}</p>
                        <p className="font-mono text-xs text-white/40 truncate">{b.product || "—"}</p>
                      </div>
                    </div>
                    <div className="col-span-3 md:col-span-3 text-right md:text-left">
                      <p className="font-mono text-lg leading-none">{b.quantity}</p>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">codes</p>
                    </div>
                    <div className="col-span-3 md:col-span-4 text-right">
                      <p className="font-mono text-sm text-primary">{b.allocated} allocated</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
